<template>
  <div>
    <div>
      <base-header class="pb-6">
        <div class="row align-items-center py-4">
          <div class="col-lg-6 col-7">
            <h6 class="h2 text-white d-inline-block mb-0"></h6>
          </div>
        </div>
      </base-header>
      <div class="container-fluid mt--6">
        <div class="card mb-4">
          <div class="card-header mt--4 mb--4">
            <div class="row">
              <div class="col">
                <h3 class="mb-0">{{ tt('vendor') }}</h3>
              </div>
              <div class="col-6">
                <div class="form-group">
                  <input type="text" class="form-control form-control-sm text-center border-radius-20"
                    v-model="table.search" :placeholder="tt('search_vendor')" v-on:keyup="filter" />
                </div>
              </div>
              <div class="col text-right">
                <base-button size="sm" type="default" @click="create" v-if="authUserPermission['abbreviation-create']">
                  {{ tt('add_vendor') }}
                </base-button>
                <router-link :to="{ path: '/vendor/vendor-profiling' }" class="btn btn-sm btn-default" tag="button">{{
                  tt('vendor_profiling') }}</router-link>
              </div>
            </div>
          </div>
          <div>
            <el-table height="350px" class="table-responsive table-flush custom-row-height"
              header-row-class-name="thead-light" :data="table.data" v-if="!onLoad">
              <el-table-column :label="tt('vendor_number')" :prop="tt('vendor_number')" min-width="122px" sortable>
                <template v-slot="{ row }">
                  {{ row.vendor_number }}
                </template>
              </el-table-column>
              <el-table-column :label="tt('name')" :prop="tt('name')" min-width="140px" sortable>
                <template v-slot="{ row }">
                  {{ row.name }}
                </template>
              </el-table-column>
              <el-table-column :label="tt('npwp')" :prop="tt('npwp')" min-width="140px" sortable>
                <template v-slot="{ row }">
                  {{ row.npwp }}
                </template>
              </el-table-column>
              <el-table-column :label="tt('nib')" :prop="tt('nib')" min-width="140px" sortable>
                <template v-slot="{ row }">
                  {{ row.nib }}
                </template>
              </el-table-column>
              <el-table-column :label="tt('pic_name')" :prop="tt('pic_name')" min-width="140px" sortable>
                <template v-slot="{ row }">
                  {{ row.pic_name }}
                </template>
              </el-table-column>
              <el-table-column prop="action" width="100">
                <template v-slot="{ row }">
                  <el-dropdown trigger="click" class="dropdown" @command="handleTableAction" :hide-on-click="true">
                    <span class="btn btn-sm btn-icon-only text-light">
                      <i class="fas fa-ellipsis-v mt-2"></i>
                    </span>
                    <el-dropdown-menu class="dropdown-menu dropdown-menu-arrow show" slot="dropdown">
                      <el-dropdown-item :command="{ action: 'edit', data: row.vendor_number }">{{ tt('edit') }}
                      </el-dropdown-item>
                      <el-dropdown-item :command="{ action: 'remove', data: row.vendor_number }">{{ tt('delete') }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </template>
              </el-table-column>
            </el-table>
            <page-loading v-else />
          </div>
          <div class="card-footer pb-0 ">
            <span class="float-left">
              <base-pagination :page-count="table.page.last_page" v-model="table.page.current_page"
                @click.native="changePage(table.page.current_page)" size="sm"></base-pagination>
            </span>
            <span class="float-right">
              {{ tt('page_info', {
                size: (table.page.current_page - 1) * table.page.per_page + table.data.length, total:
                  table.total
              }) }}
            </span>
          </div>
        </div>
      </div>
      <validation-observer v-slot="{ invalid }">
        <modal :show.sync="form.show" size="xl">
          <template slot="header">
            <h5 class="modal-title">{{ form.title }}</h5>
          </template>
          <div class="row g-3">
            <!-- Left Column -->
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('type') }} <span class="text-danger">*</span></label>
                <base-input :name="tt('type')" :placeholder="tt('type')" v-model="vendor.type" rules="required"
                  @input="formatType" />
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('name') }} <span class="text-danger">*</span></label>
                <div class="position-relative">
                  <base-input :name="tt('name')" :placeholder="tt('name')" v-model="vendor.name" rules="required"
                    @input="formatName" />
                  <div v-if="isSearching" class="position-absolute"
                    style="right: 10px; top: 50%; transform: translateY(-50%);">
                    <i class="fas fa-spinner fa-spin text-primary"></i>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('address1') }} <span
                    class="text-danger">*</span></label>
                <base-input :name="tt('address1')" :placeholder="tt('address1')" v-model="vendor.address1"
                  rules="required" />
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('address2') }}</label>
                <base-input :name="tt('address2')" :placeholder="tt('address2')" v-model="vendor.address2" />
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('postal_code') }}</label>
                <base-input :name="tt('postal_code')" :placeholder="tt('postal_code')" v-model="vendor.postal_code" />
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('country') }} <span class="text-danger">*</span></label>
                <el-select v-model="selectedCountry" placeholder="Select Country" class="w-100" clearable filterable>
                  <el-option v-for="c in countries" :key="c.value" :label="c.label" :value="c.value" />
                </el-select>
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('city') }}</label>
                <el-select v-model="vendor.city" placeholder="Select City" class="w-100" clearable filterable
                  :loading="loadingCities" :disabled="loadingCities">
                  <el-option v-for="c in cities" :key="c.value" :label="c.label" :value="c.value" />
                </el-select>
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('pic_name') }} <span class="text-danger">*</span></label>
                <base-input :name="tt('pic_name')" :placeholder="tt('pic_name')" v-model="vendor.pic_name"
                  rules="required" @input="formatPicName" />
              </div>

              <!-- <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('province') }}</label>
                <el-select v-model="selectedProvince" placeholder="Select Province" class="w-100" clearable filterable
                  :loading="loadingProvinces" :disabled="loadingProvinces">
                  <el-option v-for="p in provinces" :key="p.value" :label="p.label" :value="p.value" />
                </el-select>
              </div> -->

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('pic_email') }}</label>
                <base-input :name="tt('pic_email')" :placeholder="tt('pic_email')" v-model="vendor.pic_email"
                  type="email" @input="validatePicEmail" />
              </div>

              <!-- <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('phone') }}</label>
                <base-input :name="tt('phone')" :placeholder="tt('phone')" v-model="vendor.phone" />
              </div> -->

                             <div class="mb-3">
                 <label class="form-control-label fw-bold">{{ tt('email') }}</label>
                 <div class="position-relative">
                   <base-input :name="tt('email')" :placeholder="tt('email')" v-model="vendor.email" type="email"
                     @input="validateEmail" />
                   <div v-if="isSearching" class="position-absolute"
                     style="right: 10px; top: 50%; transform: translateY(-50%);">
                     <i class="fas fa-spinner fa-spin text-primary"></i>
                   </div>
                 </div>
               </div>
            </div>

            <!-- Right Column -->
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('npwp') }} <span class="text-danger">*</span></label>
                <div class="position-relative">
                  <base-input :name="tt('npwp')" :placeholder="tt('npwp')" v-model="vendor.npwp" rules="required" @input="formatNpwp" />
                  <div v-if="isSearching" class="position-absolute"
                    style="right: 10px; top: 50%; transform: translateY(-50%);">
                    <i class="fas fa-spinner fa-spin text-primary"></i>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('nib') }} <span class="text-danger">*</span></label>
                <div class="position-relative">
                  <base-input :name="tt('nib')" :placeholder="tt('nib')" v-model="vendor.nib" rules="required" />
                  <div v-if="isSearching" class="position-absolute"
                    style="right: 10px; top: 50%; transform: translateY(-50%);">
                    <i class="fas fa-spinner fa-spin text-primary"></i>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('bank_name') }} <span class="text-danger">*</span></label>
                <base-input :name="tt('bank_name')" :placeholder="tt('bank_name')" v-model="vendor.bank_name"
                  rules="required" @input="formatBankName" />
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('bank_account_number') }} <span class="text-danger">*</span></label>
                <base-input :name="tt('bank_account_number')" :placeholder="tt('bank_account_number')"
                  v-model="vendor.bank_account_number" rules="required" />
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('bank_account_name') }}</label>
                <base-input :name="tt('bank_account_name')" :placeholder="tt('bank_account_name')"
                  v-model="vendor.bank_account_name" @input="formatBankAccountName" />
              </div>

              <!-- <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('pic_name') }}</label>
                <base-input :name="tt('pic_name')" :placeholder="tt('pic_name')" v-model="vendor.pic_name"
                  @input="formatPicName" />
              </div> -->
              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('province') }}</label>
                <el-select v-model="selectedProvince" placeholder="Select Province" class="w-100" clearable filterable
                  :loading="loadingProvinces" :disabled="loadingProvinces">
                  <el-option v-for="p in provinces" :key="p.value" :label="p.label" :value="p.value" />
                </el-select>
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('status') }} <span class="text-danger">*</span></label>
                <el-select v-model="vendor.status" :placeholder="tt('status')" class="w-100" clearable>
                  <el-option label="Active" value="active" />
                  <el-option label="Inactive" value="inactive" />
                  <el-option label="Block" value="block" />
                </el-select>
              </div>

              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('pic_phone') }} <span class="text-danger">*</span></label>
                <base-input :name="tt('pic_phone')" :placeholder="tt('pic_phone')" v-model="vendor.pic_phone" rules="required" />
              </div>

              <!-- <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('pic_email') }}</label>
                <base-input :name="tt('pic_email')" :placeholder="tt('pic_email')" v-model="vendor.pic_email"
                  type="email" @input="validatePicEmail" />
              </div> -->
              <div class="mb-3">
                <label class="form-control-label fw-bold">{{ tt('phone') }}</label>
                <div class="position-relative">
                  <base-input :name="tt('phone')" :placeholder="tt('phone')" v-model="vendor.phone" />
                  <div v-if="isSearching" class="position-absolute"
                    style="right: 10px; top: 50%; transform: translateY(-50%);">
                    <i class="fas fa-spinner fa-spin text-primary"></i>
                  </div>
                </div>
              </div>

            </div>
          </div>
          <template slot="footer">
            <base-button type="secondary" @click="form.show = false">{{ tt('close') }}</base-button>
            <base-button type="primary" v-on:click="save" :disabled="btnSave.onLoading || invalid">
              <span v-if="btnSave.onLoading"><i class="fas fa-spinner fa-spin"></i> {{ tt('please_wait') }}</span>
              <span v-else>
                <span v-if="form.add">{{ tt('add') }}</span>
                <span v-else>{{ tt('edit') }}</span>
              </span>
            </base-button>
          </template>
        </modal>
      </validation-observer>
    </div>
    <!--        <noaccess v-else/>-->
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Api from '@/helpers/api';
import vendor from '@/services/vendor/vendor.service';
import axios from 'axios';
import dialog from '@/helpers/dialog';
import swal from 'sweetalert2';

export default {
  data() {
    return {
      onLoad: true,
      loadTimeout: null,
      btnSave: {
        onLoading: false
      },
      form: {
        title: this.tt('add_vendor'),
        show: false
      },
      table: {
        search: '',
        total: 0,
        data: [],
        page: {
          current_page: 1,
          per_page: 100,
        },
      },
      errorMessage: null,
      vendor: {},

      // NEW for country/province/city dropdown
      countries: [],
      provinces: [],
      cities: [],
      selectedCountry: null,
      selectedProvince: null,
      // Loading states
      loadingProvinces: false,
      loadingCities: false,

      // Search debounce
      searchTimeout: null,
      isSearching: false,
    }
  },
  computed: {
    ...mapState({
      authUserPermission: state => state.auth.userPermission
    }),
  },
  watch: {
    selectedCountry(newVal) {
      if (newVal) {
        this.vendor.country = newVal.toUpperCase();
        this.fetchProvinces(newVal);
      } else {
        this.provinces = [];
        this.cities = [];
      }
    },
    selectedProvince(newVal) {
      if (newVal) {
        this.vendor.province = newVal.toUpperCase();
        this.fetchCities(this.selectedCountry, newVal);
      } else {
        this.cities = [];
      }
    },
    'vendor.city'(newVal) {
      if (newVal) {
        this.vendor.city = newVal.toUpperCase();
      }
    },
    'vendor.name'(newVal) {
      if (newVal) {
        this.formatName();
        // Search for similar vendors only when adding new vendor (not editing)
        if (this.vendor.name.length >= 3 && this.form.show && this.form.add) {
          this.searchSimilarVendors('name');
        }
      }
    },
    'vendor.bank_name'(newVal) {
      if (newVal) {
        this.formatBankName();
      }
    },
    'vendor.bank_account_name'(newVal) {
      if (newVal) {
        this.formatBankAccountName();
      }
    },
    'vendor.pic_name'(newVal) {
      if (newVal) {
        this.formatPicName();
      }
    },
    'vendor.type'(newVal) {
      if (newVal) {
        this.formatType();
      }
    },
    'vendor.npwp'(newVal) {
      if (newVal && newVal.length >= 3 && this.form.show && this.form.add) {
        this.searchSimilarVendors('npwp');
      }
    },
    'vendor.nib'(newVal) {
      if (newVal && newVal.length >= 3 && this.form.show && this.form.add) {
        this.searchSimilarVendors('nib');
      }
    },
    'vendor.phone'(newVal) {
      if (newVal && newVal.length >= 3 && this.form.show && this.form.add) {
        this.searchSimilarVendors('phone');
      }
    },
    'vendor.email'(newVal) {
      if (newVal && newVal.length >= 3 && this.form.show && this.form.add) {
        this.searchSimilarVendors('email');
      }
    }
  },
  mounted() {
    this.get();
    
    // // Test if SweetAlert2 is available
    // console.log('SweetAlert2 available:', typeof swal !== 'undefined');
    // if (typeof swal !== 'undefined') {
    //   console.log('SweetAlert2 version:', swal.version);
    // }
  },
  beforeDestroy() {
    // Clear any pending timeouts
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    async fetchCountries() {
      try {
        const res = await axios.get('https://countriesnow.space/api/v0.1/countries/iso');
        this.countries = res.data.data.map(item => ({
          label: item.name,
          value: item.name,
        }));
      } catch (err) {
        console.error('Error fetching countries', err);
      }
    },
    async fetchProvinces(country) {
      this.loadingProvinces = true;
      this.loadingCities = true;
      try {
        const res = await axios.post('https://countriesnow.space/api/v0.1/countries/states', {
          country
        });
        this.provinces = res.data.data.states.map(item => ({
          label: item.name,
          value: item.name,
        }));
      } catch (err) {
        console.error('Error fetching provinces', err);
      } finally {
        this.loadingProvinces = false;
        this.loadingCities = false;
      }
    },
    async fetchCities(country, state) {
      this.loadingCities = true;
      try {
        const res = await axios.post('https://countriesnow.space/api/v0.1/countries/state/cities', {
          country,
          state
        });
        this.cities = res.data.data.map(item => ({
          label: item,
          value: item
        }));
      } catch (err) {
        console.error('Error fetching cities', err);
      } finally {
        this.loadingCities = false;
      }
    },

    formatName() {
      if (this.vendor.name) {
        // Remove symbols except letters, numbers, and spaces
        let formatted = this.vendor.name.replace(/[^a-zA-Z0-9\s]/g, '');

        // Remove double spaces but keep single spaces
        formatted = formatted.replace(/\s+/g, ' ');

        // Convert to uppercase
        this.vendor.name = formatted.toUpperCase();
      }
    },

    validateEmail() {
      if (this.vendor.email && this.vendor.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.vendor.email)) {

        }
      }
    },

    validatePicEmail() {
      if (this.vendor.pic_email && this.vendor.pic_email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.vendor.pic_email)) {

        }
      }
    },

    formatNpwp() {
      if (this.vendor.npwp) {
        // Remove all non-numeric characters
        let npwp = this.vendor.npwp.replace(/\D/g, '');

        // Format NPWP: XX.XXX.XXX.X-XXX.XXX
        if (npwp.length <= 15) {
          let formatted = '';
          for (let i = 0; i < npwp.length; i++) {
            if (i === 2 || i === 5 || i === 8 || i === 9 || i === 12) {
              formatted += '.';
            }
            if (i === 8) {
              formatted += '-';
            }
            formatted += npwp[i];
          }
          this.vendor.npwp = formatted;
        }
      }
    },

    // Get NPWP without formatting for backend
    getNpwpForBackend() {
      if (this.vendor.npwp) {
        return this.vendor.npwp.replace(/\D/g, '');
      }
      return this.vendor.npwp;
    },

    formatBankName() {
      if (this.vendor.bank_name) {
        // Remove symbols except letters, numbers, and spaces
        let formatted = this.vendor.bank_name.replace(/[^a-zA-Z0-9\s]/g, '');

        // Remove double spaces but keep single spaces
        formatted = formatted.replace(/\s+/g, ' ');

        // Convert to uppercase
        this.vendor.bank_name = formatted.toUpperCase();
      }
    },

    formatBankAccountName() {
      if (this.vendor.bank_account_name) {
        // Remove symbols except letters, numbers, and spaces
        let formatted = this.vendor.bank_account_name.replace(/[^a-zA-Z0-9\s]/g, '');

        // Remove double spaces but keep single spaces
        formatted = formatted.replace(/\s+/g, ' ');

        // Convert to uppercase
        this.vendor.bank_account_name = formatted.toUpperCase();
      }
    },

    formatPicName() {
      if (this.vendor.pic_name) {
        // Remove symbols except letters, numbers, and spaces
        let formatted = this.vendor.pic_name.replace(/[^a-zA-Z0-9\s]/g, '');

        // Remove double spaces but keep single spaces
        formatted = formatted.replace(/\s+/g, ' ');

        // Convert to uppercase
        this.vendor.pic_name = formatted.toUpperCase();
      }
    },

    formatType() {
      if (this.vendor.type) {
        // Remove symbols except letters, numbers, and spaces
        let formatted = this.vendor.type.replace(/[^a-zA-Z0-9\s]/g, '');

        // Remove double spaces but keep single spaces
        formatted = formatted.replace(/\s+/g, ' ');

        // Convert to uppercase
        this.vendor.type = formatted.toUpperCase();
      }
    },

    async searchSimilarVendors(searchField = null) {
      // Prevent multiple simultaneous searches
      if (this.isSearching) {
        return;
      }

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout for debounce
      this.searchTimeout = setTimeout(async () => {
        this.isSearching = true;
        try {
          // Determine which field to search based on what was last updated
          let searchValue = '';
          
          if (searchField === 'name' && this.vendor.name && this.vendor.name.length >= 3) {
            searchValue = this.vendor.name;
          } else if (searchField === 'npwp' && this.vendor.npwp && this.vendor.npwp.length >= 3) {
            searchValue = this.getNpwpForBackend(); // Use unformatted NPWP for search
          } else if (searchField === 'nib' && this.vendor.nib && this.vendor.nib.length >= 3) {
            searchValue = this.vendor.nib;
          } else if (searchField === 'phone' && this.vendor.phone && this.vendor.phone.length >= 3) {
            searchValue = this.vendor.phone;
          } else if (searchField === 'email' && this.vendor.email && this.vendor.email.length >= 3) {
            searchValue = this.vendor.email;
          }

          // Only search if we have a valid search value
          if (!searchValue) {
            this.isSearching = false;
            return;
          }

          // console.log('Searching for similar vendors with field:', searchField, 'value:', searchValue);
          const response = await vendor.search({
            search: searchValue
          });
          
          // console.log('Search response:', response.data);
          
          if (response.data.status === 'Success') {
            if (response.data.data.length > 0) {
              // console.log('Found similar vendors, showing modal');
              this.showSimilarVendorsModal(response.data);
            } 
            // else {
            //   console.log('No similar vendors found');
            // }
            // Optional: Show message if no similar vendors found
            // else if (response.data.message) {
            //   this.$notify({
            //     message: 'No similar vendors found',
            //     type: 'info',
            //     verticalAlign: 'bottom',
            //     horizontalAlign: 'left'
            //   });
            // }
          }
        } catch (error) {
          console.error('Error searching similar vendors:', error);
        } finally {
          this.isSearching = false;
        }
      }, 500); // 500ms delay
    },

    showSimilarVendorsModal(response) {
      // console.log('showSimilarVendorsModal called with:', response);
      
      // Create HTML table for the results
      let tableHtml = `
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Vendor Number</th>
                <th>Name</th>
                <th>Type</th>
                <th>Address</th>
                <th>Contact</th>
                <th>Additional Info</th>
                <th>Status</th>
                <th>Match Details</th>
              </tr>
            </thead>
            <tbody>
      `;

      response.data.forEach(vendor => {
        const contactInfo = [];
        if (vendor.email) contactInfo.push(`Email: ${vendor.email}`);
        if (vendor.phone) contactInfo.push(`Phone: ${vendor.phone}`);
        const contactDisplay = contactInfo.length > 0 ? contactInfo.join('<br>') : '-';

        const additionalInfo = [];
        if (vendor.npwp) additionalInfo.push(`NPWP: ${vendor.npwp}`);
        if (vendor.nib) additionalInfo.push(`NIB: ${vendor.nib}`);
        const additionalDisplay = additionalInfo.length > 0 ? additionalInfo.join('<br>') : '-';

        const statusBadge = vendor.status ?
          `<span class="badge badge-${vendor.status === 'active' ? 'success' : vendor.status === 'inactive' ? 'secondary' : 'danger'}">${vendor.status.toUpperCase()}</span>` :
          '-';

        // Build match details
        const matchDetails = [];
        if (vendor.match_percent_name && parseFloat(vendor.match_percent_name) > 0) {
          matchDetails.push(`Name: ${parseFloat(vendor.match_percent_name).toFixed(1)}%`);
        }
        if (vendor.match_npwp_percent && parseFloat(vendor.match_npwp_percent) > 0) {
          matchDetails.push(`NPWP: ${parseFloat(vendor.match_npwp_percent).toFixed(1)}%`);
        }
        if (vendor.match_nib_percent && parseFloat(vendor.match_nib_percent) > 0) {
          matchDetails.push(`NIB: ${parseFloat(vendor.match_nib_percent).toFixed(1)}%`);
        }
        if (vendor.match_phone_percent && parseFloat(vendor.match_phone_percent) > 0) {
          matchDetails.push(`Phone: ${parseFloat(vendor.match_phone_percent).toFixed(1)}%`);
        }
        if (vendor.match_email_percent && parseFloat(vendor.match_email_percent) > 0) {
          matchDetails.push(`Email: ${parseFloat(vendor.match_email_percent).toFixed(1)}%`);
        }

        const matchDisplay = matchDetails.length > 0 ? matchDetails.join('<br>') : 'No matches';

        // Calculate overall match percentage (average of all matches)
        const matchPercentages = [
          parseFloat(vendor.match_percent_name || 0),
          parseFloat(vendor.match_npwp_percent || 0),
          parseFloat(vendor.match_nib_percent || 0),
          parseFloat(vendor.match_phone_percent || 0),
          parseFloat(vendor.match_email_percent || 0)
        ].filter(percent => percent > 0);

        const overallMatch = matchPercentages.length > 0 
          ? (matchPercentages.reduce((sum, percent) => sum + percent, 0) / matchPercentages.length).toFixed(1)
          : 0;

        tableHtml += `
          <tr>
            <td>${vendor.vendor_number}</td>
            <td><strong>${vendor.name}</strong></td>
            <td>${vendor.type || '-'}</td>
            <td>${vendor.address1 || '-'}</td>
            <td style="font-size: 0.8rem;">${contactDisplay}</td>
            <td style="font-size: 0.8rem;">${additionalDisplay}</td>
            <td>${statusBadge}</td>
            <td style="font-size: 0.8rem;">
              <div class="mb-1">
                <span class="badge badge-${this.getMatchBadgeColor(overallMatch)}">Overall: ${overallMatch}%</span>
              </div>
              <div style="font-size: 0.75rem;">
                ${matchDisplay}
              </div>
            </td>
          </tr>
        `;
      });

      tableHtml += `
            </tbody>
          </table>
        </div>
      `;

      // console.log('About to show SweetAlert modal');
      
      // Try different ways to call SweetAlert
      const sweetAlert = this.$swal || swal;

      // Then try SweetAlert
      try {
        sweetAlert.fire({
          title: 'Similar Vendors Found',
          html: `
            <div class="text-left">
              <div class="alert alert-warning mb-3">
                <i class="fas fa-exclamation-triangle"></i> 
                <strong>Similar vendors found!</strong>
                <br>
                <small style="color: white;">Found ${response.data.length} similar vendor(s). Please review the data below.</small>
                ${this.hasHighSimilarity(response.data) ? '<br><small><i class="fas fa-exclamation-circle"></i> <strong>Warning:</strong> Some vendors have very high similarity (>80%)</small>' : ''}
              </div>
              ${tableHtml}
              <div class="alert alert-info mt-3">
                <i class="fas fa-info-circle"></i>
                <strong>What happens next?</strong>
                <ul class="mb-0 mt-2">
                  <li><strong>Continue with Already:</strong> Close this dialog, because the vendor already exists</li>
                  <li><strong>Cancel:</strong> Cancel the operation and review the data</li>
                </ul>
              </div>
            </div>
          `,
          icon: 'info',
          confirmButtonText: 'Continue with Already',
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          width: '800px',
          customClass: {
            container: 'similar-vendors-modal'
          }
        }).then((result) => {
          if (result.isConfirmed) {
            this.$notify({
              message: 'Vendor already exists. No need to create a new one.',
              type: 'info',
              verticalAlign: 'bottom',
              horizontalAlign: 'left'
            });
            this.form.show = false;
          }
          // If cancel, do nothing (just close modal)
        });
      } catch (error) {
        console.error('Error showing SweetAlert:', error);
      }
    },

    getMatchBadgeColor(matchPercent) {
      const percent = parseFloat(matchPercent);
      if (percent >= 80) return 'danger';
      if (percent >= 60) return 'warning';
      if (percent >= 40) return 'info';
      return 'secondary';
    },

    getMatchDescription(matchPercent) {
      const percent = parseFloat(matchPercent);
      if (percent >= 80) return 'High similarity';
      if (percent >= 60) return 'Medium similarity';
      if (percent >= 40) return 'Low similarity';
      return 'Very low similarity';
    },

    hasHighSimilarity(vendors) {
      return vendors.some(vendor => {
        const matchPercentages = [
          parseFloat(vendor.match_percent_name || 0),
          parseFloat(vendor.match_npwp_percent || 0),
          parseFloat(vendor.match_nib_percent || 0),
          parseFloat(vendor.match_phone_percent || 0),
          parseFloat(vendor.match_email_percent || 0)
        ].filter(percent => percent > 0);

        const overallMatch = matchPercentages.length > 0 
          ? (matchPercentages.reduce((sum, percent) => sum + percent, 0) / matchPercentages.length)
          : 0;

        return overallMatch >= 80;
      });
    },

    get() {
      let context = this;
      Api(context, vendor.get(this.table.page.current_page, { search: this.table.search }))
        .onSuccess(function (response) {
          context.table.total = response.data.data.data.total;
          context.table.data = response.data.data.data.data;
          context.holdingCode = response.data.data.client;
          context.table.page = response.data.data.data;
          context.onLoad = false;
        })
        .onError(function (error) {
          if (error.response.status == 404) {
            context.table.data = [];
            context.table.total = 0;
            context.onLoad = false;
          }
        })
        .call();
    },
    filter() {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = setTimeout(() => {
        this.get();
      }, 1000);
    },
    handleTableAction(command) {
      switch (command.action) {
        case 'edit':
          this.edit(command.data);
          break;
        case 'remove':
          this.remove(command.data);
          break;
        default:
          break;
      }
    },
    create() {
      this.errorMessage = null;
      this.form.add = true;
      this.form.title = this.tt('add_vendor');
      this.form.show = true;
      this.vendor = {};
      this.selectedCountry = null;
      this.selectedProvince = null;
      this.countries = [];
      this.provinces = [];
      this.cities = [];
      this.loadingProvinces = false;
      this.loadingCities = false;
      this.fetchCountries();
    },
    edit(id) {
      this.errorMessage = null;
      let context = this;
      Api(context, vendor.show(id)).onSuccess(function (response) {
        context.vendor = response.data.data;
        context.form.add = false;
        context.form.title = context.tt('edit_vendor');
        context.form.show = true;

        // Format NPWP for display if it exists
        if (context.vendor.npwp) {
          context.formatNpwp();
        }

        // Initialize selected values for editing
        context.selectedCountry = context.vendor.country;
        context.fetchCountries().then(() => {
          context.fetchProvinces(context.vendor.country).then(() => {
            context.selectedProvince = context.vendor.province;
            context.fetchCities(context.vendor.country, context.vendor.province);
          });
        });
      }).call();
    },
    save() {
      // Validate required fields
      if (!this.vendor.country) {
        this.$notify({
          message: 'Country is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Validate name field
      if (!this.vendor.name || this.vendor.name.trim() === '') {
        this.$notify({
          message: 'Name is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // validate status
      if (!this.vendor.status) {
        this.$notify({
          message: 'Status is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Validate NPWP
      if (!this.vendor.npwp || this.vendor.npwp.trim() === '') {
        this.$notify({
          message: 'NPWP is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Validate NIB
      if (!this.vendor.nib || this.vendor.nib.trim() === '') {
        this.$notify({
          message: 'NIB is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Validate Bank Name
      if (!this.vendor.bank_name || this.vendor.bank_name.trim() === '') {
        this.$notify({
          message: 'Bank Name is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Validate Bank Account Number
      if (!this.vendor.bank_account_number || this.vendor.bank_account_number.trim() === '') {
        this.$notify({
          message: 'Bank Account Number is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Validate PIC Name
      if (!this.vendor.pic_name || this.vendor.pic_name.trim() === '') {
        this.$notify({
          message: 'PIC Name is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Validate PIC Phone
      if (!this.vendor.pic_phone || this.vendor.pic_phone.trim() === '') {
        this.$notify({
          message: 'PIC Phone is required',
          type: 'error',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        return;
      }

      // Format name and type before saving
      this.formatName();
      this.formatType();

      // Trim the name and type before saving to remove leading/trailing spaces
      if (this.vendor.name) {
        this.vendor.name = this.vendor.name.trim();
      }
      if (this.vendor.type) {
        this.vendor.type = this.vendor.type.trim();
      }

      // Validate email fields
      if (this.vendor.email && this.vendor.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.vendor.email)) {
          this.$notify({
            message: 'Please enter a valid email address',
            type: 'error',
            verticalAlign: 'bottom',
            horizontalAlign: 'left'
          });
          this.btnSave.onLoading = false;
          return;
        }
      }

      if (this.vendor.pic_email && this.vendor.pic_email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.vendor.pic_email)) {
          this.$notify({
            message: 'Please enter a valid PIC email address',
            type: 'error',
            verticalAlign: 'bottom',
            horizontalAlign: 'left'
          });
          this.btnSave.onLoading = false;
          return;
        }
      }

      // Ensure all location fields are uppercase
      if (this.vendor.province) {
        this.vendor.province = this.vendor.province.toUpperCase();
      }
      if (this.vendor.city) {
        this.vendor.city = this.vendor.city.toUpperCase();
      }
      if (this.vendor.country) {
        this.vendor.country = this.vendor.country.toUpperCase();
      }

      // Create a copy of vendor data for backend with unformatted NPWP
      let vendorDataForBackend = { ...this.vendor };
      if (vendorDataForBackend.npwp) {
        vendorDataForBackend.npwp = this.getNpwpForBackend();
      }

      this.btnSave.onLoading = true;
      let api = this.form.add
        ? Api(this, vendor.create(vendorDataForBackend))
        : Api(this, vendor.update(vendorDataForBackend.vendor_number, vendorDataForBackend));

      api.onSuccess((response) => {
        this.get();
        this.$notify({
          message: response.data.message,
          type: 'success',
          verticalAlign: 'bottom',
          horizontalAlign: 'left'
        });
        this.form.show = false;
      }).onError((error) => {
        this.errorMessage = error.response.data;
      }).onFinish(() => {
        this.btnSave.onLoading = false;
      }).call();
    },
    remove(id) {
      this.confirmDialog(this.tt('confirm_delete')).then((result) => {
        if (result.value) {
          Api(this, vendor.delete(id))
            .onSuccess((response) => {
              this.$notify({
                message: response.data.message,
                type: 'success',
                verticalAlign: 'bottom',
                horizontalAlign: 'left'
              });
              this.get();
            }).call();
        }
      });
    },
    changePage(page) {
      this.onLoad = true;
      clearTimeout(this.loadTimeout);
      this.loadTimeout = setTimeout(() => {
        this.get();
      }, 100);
    },
  }
};
</script>
<style scope>
.similar-vendors-modal .swal2-popup {
  max-width: 1000px;
  width: 90% !important;
}

.similar-vendors-modal .table {
  font-size: 0.875rem;
  margin-bottom: 0;
}

.similar-vendors-modal .table th {
  background-color: #f8f9fa;
  border-color: #dee2e6;
  font-weight: 600;
  font-size: 0.8rem;
}

.similar-vendors-modal .table td {
  vertical-align: middle;
  border-color: #dee2e6;
}

.similar-vendors-modal .badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.similar-vendors-modal .swal2-content {
  padding: 0;
}

.similar-vendors-modal .swal2-html-container {
  margin: 1rem 0;
}

.custom-row-height .el-table__row {
  height: 70px !important;
}
</style>
